import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { renderContent } from '../renderContent';
import { apiClient } from '../../utils/apiClient';

function processMessages(messages) {
    const processed = [];
    for (const msg of messages) {
        if (msg.text?.trim()) {
            processed.push({ type: 'text', role: msg.role, content: msg.text, timestamp: msg.timestamp });
        }
        for (const tool of (msg.tools ?? [])) {
            if (tool.name === 'display_action_button') {
                processed.push({ type: 'button', role: msg.role, label: tool.args?.label, timestamp: msg.timestamp });
            }
        }
    }
    return processed;
}

function SessionDetail({ convId, onBack }) {
    const [data, setData]           = useState(null);
    const [loading, setLoading]     = useState(true);
    const [clickedIdx, setClickedIdx] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_conversations.php?id=${convId}`, { method: 'GET' });
                const json = await res.json();
                if (json.success) {
                    setData({ conversation: json.conversation, messages: processMessages(json.messages) });
                }
            } catch {}
            setLoading(false);
        };
        load();
    }, [convId]);

    return (
        <div className="flex flex-col h-full">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors mb-6 font-semibold"
            >
                ← Retour à l'historique
            </button>

            {loading && (
                <div className="flex-1 flex items-center justify-center text-gray-400">Chargement...</div>
            )}

            {!loading && data && (
                <>
                    <div className="mb-6">
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white">{data.conversation.title}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(data.conversation.created_at).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 pb-8">
                        {data.messages.map((msg, idx) => {
                            const msgs = data.messages;
                            const showName      = idx === 0 || msgs[idx - 1].role !== msg.role;
                            const isLastInGroup = idx === msgs.length - 1 || msgs[idx + 1].role !== msg.role;

                            // Find group boundaries for clicked index
                            let groupStart = idx;
                            while (groupStart > 0 && msgs[groupStart - 1].role === msg.role) groupStart--;

                            const showTimestamp = isLastInGroup && msg.timestamp &&
                                clickedIdx !== null && clickedIdx >= groupStart && clickedIdx <= idx;

                            return (
                                <div key={idx} className={`flex flex-col w-full ${msg.role === 'user' ? 'items-end' : 'items-start'} ${showName && idx !== 0 ? 'mt-4' : 'mt-1'}`}>
                                    {showName && (
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1 px-1 font-bold">
                                            {msg.role === 'user' ? 'Toi' : 'Axio'}
                                        </span>
                                    )}
                                    {msg.type === 'text' && (
                                        <div
                                            onClick={() => setClickedIdx(clickedIdx === idx ? null : idx)}
                                            className={`p-4 rounded-2xl text-sm leading-relaxed max-w-[85%] shadow-sm cursor-pointer select-none ${msg.role === 'user' ? 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-900 dark:text-cyan-50 rounded-tr-sm' : 'bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-800 dark:text-gray-200 rounded-tl-sm'}`}
                                        >
                                            {renderContent(msg.content)}
                                        </div>
                                    )}
                                    {msg.type === 'button' && (
                                        <div
                                            onClick={() => setClickedIdx(clickedIdx === idx ? null : idx)}
                                            className="px-4 py-2 mt-1 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 opacity-60 cursor-pointer"
                                        >
                                            {msg.label}
                                        </div>
                                    )}
                                    {showTimestamp && (
                                        <div className={`flex items-center gap-1 mt-1 text-[10px] text-gray-400 dark:text-gray-600 transition-opacity duration-200 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                            </svg>
                                            {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {data.messages.length === 0 && (
                            <p className="text-gray-400 text-sm text-center py-8">Aucun message dans cette session.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function ConversationHistory({ onExit }) {
    const navigate                          = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading]             = useState(true);
    const [hasMore, setHasMore]             = useState(false);
    const [page, setPage]                   = useState(1);
    const [selectedId, setSelectedId]       = useState(null);

    const handleSelect = (conv) => {
        if (conv.status === 'active') {
            navigate('/');
            window.dispatchEvent(new Event('open-axio-chat'));
            return;
        }
        setSelectedId(conv.id);
    };

    const load = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_conversations.php?page=${p}`, { method: 'GET' });
            const json = await res.json();
            if (json.success) {
                setConversations(prev => p === 1 ? json.conversations : [...prev, ...json.conversations]);
                setHasMore(json.has_more);
                setPage(p);
            }
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { load(1); }, [load]);

    if (selectedId) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-8">
                <SessionDetail convId={selectedId} onBack={() => setSelectedId(null)} />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={onExit}
                    className="text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
                    title="Retour"
                >
                    ←
                </button>
                <div>
                    <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white">Historique des sessions</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Toutes tes sessions passées avec Axio</p>
                </div>
            </div>

            {loading && conversations.length === 0 && (
                <div className="text-center text-gray-400 py-16">Chargement...</div>
            )}

            {!loading && conversations.length === 0 && (
                <div className="text-center text-gray-400 py-16">
                    <p className="text-4xl mb-4">💬</p>
                    <p className="font-semibold">Aucune session passée pour l'instant.</p>
                    <p className="text-sm mt-1">Les sessions terminées apparaîtront ici.</p>
                </div>
            )}

            <div className="flex flex-col gap-3">
                {conversations.map(conv => {
                    const date    = new Date(conv.created_at);
                    const isToday = new Date().toDateString() === date.toDateString();
                    const isActive = conv.status === 'active';
                    return (
                        <button
                            key={conv.id}
                            onClick={() => handleSelect(conv)}
                            className={`w-full text-left backdrop-blur-xl rounded-2xl p-5 transition-all duration-200 shadow-sm group border ${isActive ? 'bg-cyan-500/5 dark:bg-cyan-500/[0.06] border-cyan-500/30 hover:border-cyan-500/50 hover:bg-cyan-500/10 dark:hover:bg-cyan-500/10' : 'bg-white/70 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.05] hover:border-cyan-500/30 hover:bg-white dark:hover:bg-white/[0.05]'}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        {isActive && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-full shrink-0">
                                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse"/>
                                                {conv.is_daily ? 'Session du jour' : 'En cours'}
                                            </span>
                                        )}
                                        <p className="font-bold text-gray-800 dark:text-white text-sm truncate group-hover:text-cyan-700 dark:group-hover:text-cyan-400 transition-colors">
                                            {conv.title ?? 'Session du jour'}
                                        </p>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {isToday ? 'Aujourd\'hui' : date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                        {' · '}
                                        {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <span className="text-gray-300 dark:text-gray-600 group-hover:text-cyan-500 transition-colors text-lg shrink-0">›</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {hasMore && (
                <div className="text-center mt-6">
                    <button
                        onClick={() => load(page + 1)}
                        disabled={loading}
                        className="px-6 py-3 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-all disabled:opacity-50"
                    >
                        {loading ? 'Chargement...' : 'Voir plus'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default ConversationHistory;
